import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Input, Button, Checkbox, Alert, Row, Col } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';
import { loginAPI } from '../../utils/api';
import { getBackendUrl } from '../../config/domains';

// 用户信息接口
interface UserInfo {
  username: string;
  storeUserName?: string;
}

interface LoginModalProps {
  visible: boolean;
  onClose: () => void;
  loginType: 'merchant' | 'company';
  onLoginSuccess: (userInfo: UserInfo) => void;
}

// 登录失败次数的存储键
const getFailCountKey = (type: 'merchant' | 'company') => `login_fail_count_${type}`;

// 生成随机验证码
const generateCaptcha = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let result = '';
  for (let i = 0; i < 4; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const LoginModal: React.FC<LoginModalProps> = ({ visible, onClose, loginType, onLoginSuccess }) => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [captcha, setCaptcha] = useState('');
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [failCount, setFailCount] = useState(0);

  // 刷新验证码
  const refreshCaptcha = useCallback(() => {
    setCaptcha(generateCaptcha());
  }, []);

  // 获取当前登录类型的失败次数
  const getFailCount = useCallback(() => {
    const key = getFailCountKey(loginType);
    const stored = localStorage.getItem(key);
    return stored ? parseInt(stored, 10) : 0;
  }, [loginType]);

  // 设置失败次数
  const setFailCountStorage = useCallback((count: number) => {
    const key = getFailCountKey(loginType);
    localStorage.setItem(key, count.toString());
    setFailCount(count);
    // 失败3次及以上显示验证码
    if (count >= 3) {
      setShowCaptcha(true);
      refreshCaptcha();
    }
  }, [loginType, refreshCaptcha]);

  // 登录成功时清除失败次数
  const clearFailCount = useCallback(() => {
    const key = getFailCountKey(loginType);
    localStorage.removeItem(key);
    setFailCount(0);
    setShowCaptcha(false);
  }, [loginType]);

  // 组件显示时检查是否需要显示验证码
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setError('');
      const count = getFailCount();
      setFailCount(count);
      if (count >= 3) {
        setShowCaptcha(true);
        refreshCaptcha();
      } else {
        setShowCaptcha(false);
      }
    }
  }, [visible, form, getFailCount, refreshCaptcha]);

  const onFinish = async (values: { username: string; password: string; isRemember: boolean; captchaInput?: string }) => {
    setError('');

    // 如果需要验证码，先验证
    if (showCaptcha) {
      if (!values.captchaInput || values.captchaInput.toUpperCase() !== captcha) {
        setError('验证码错误，请重新输入');
        refreshCaptcha();
        form.setFieldValue('captchaInput', '');
        return;
      }
    }

    setLoading(true);

    try {
      // 调用新的登录 API，传递用户类型
      const data = await loginAPI.login(values.username, values.password, values.isRemember, loginType);

      if (data.code === 1) {
        // 登录成功，清除失败次数
        clearFailCount();
        // 登录成功，传递用户信息给父组件
        if (onLoginSuccess) {
          onLoginSuccess({
            username: values.username,
            storeUserName: data.data?.store_user_name || values.username
          });
        }
        // 登录成功后直接跳转到后台
        const backendUrl = getBackendUrl();
        window.location.href = `${backendUrl}/index.php?s=/store/index/index`;
      } else {
        // 登录失败，增加失败次数
        const newFailCount = failCount + 1;
        setFailCountStorage(newFailCount);
        
        let errorMsg = data.msg || '登录失败，请检查账号和密码';
        if (newFailCount >= 3) {
          errorMsg += `（已连续失败${newFailCount}次，请输入验证码）`;
        } else if (newFailCount === 2) {
          errorMsg += `（再失败${3 - newFailCount}次将需要输入验证码）`;
        }
        setError(errorMsg);
      }
    } catch (err) {
      // 登录失败，增加失败次数
      const newFailCount = failCount + 1;
      setFailCountStorage(newFailCount);
      
      let errorMsg = '登录失败，请检查账号和密码';
      if (newFailCount >= 3) {
        errorMsg += `（已连续失败${newFailCount}次，请输入验证码）`;
      } else if (newFailCount === 2) {
        errorMsg += `（再失败${3 - newFailCount}次将需要输入验证码）`;
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={loginType === 'company' ? '装修公司登录' : '商户登录'}
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width={400}
    >
      <div className="login-modal-content">
        <p style={{ marginBottom: '24px', color: '#666' }}>
          {loginType === 'company' ? '欢迎回到同步到家商户平台' : '欢迎回到同步到家'}
        </p>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            style={{ marginBottom: '16px' }}
            onClose={() => setError('')}
          />
        )}

        <Form
          form={form}
          name="login_form"
          initialValues={{ remember: true }}
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
        >
          <Form.Item
            label="账号"
            name="username"
            rules={[{ required: true, message: '请输入您的账号!' }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="请输入账号"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="密码"
            name="password"
            rules={[{ required: true, message: '请输入您的密码!' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="请输入密码"
              size="large"
            />
          </Form.Item>

          {/* 验证码输入框 - 连续失败3次后显示 */}
          {showCaptcha && (
            <Form.Item
              label="验证码"
              name="captchaInput"
              rules={[{ required: true, message: '请输入验证码!' }]}
            >
              <Row gutter={8}>
                <Col flex="auto">
                  <Input
                    prefix={<SafetyOutlined />}
                    placeholder="请输入验证码"
                    size="large"
                    maxLength={4}
                  />
                </Col>
                <Col>
                  <Button
                    size="large"
                    onClick={refreshCaptcha}
                    style={{
                      minWidth: '100px',
                      fontFamily: 'monospace',
                      fontSize: '16px',
                      letterSpacing: '4px',
                      backgroundColor: '#f0f0f0',
                      color: '#333',
                      fontWeight: 'bold'
                    }}
                  >
                    {captcha}
                  </Button>
                </Col>
              </Row>
            </Form.Item>
          )}

          <Form.Item name="isRemember" valuePropName="checked" noStyle>
            <Checkbox>记住登录状态</Checkbox>
          </Form.Item>

          <Form.Item style={{ marginTop: '24px' }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              size="large"
            >
              {loading ? '登录中...' : '登录'}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default LoginModal;
